import {
  PlcBrand,
  extractPlcObjectDescriptors,
  type PlcObjectDescriptor,
  type PlcTargetProfile,
} from '@realplc/types';

export interface PLCBlock {
  name: string;
  code: string;
  kind: 'functionBlock' | 'function' | 'program' | 'organizationBlock' | 'dataBlock' | 'type' | 'global';
  fileName: string;
  descriptor: PlcObjectDescriptor;
}

const DEFAULT_TARGET: PlcTargetProfile = {
  brand: PlcBrand.Siemens,
  programmingModel: 'classic_plc',
  sourceEncoding: 'utf8_bom',
};

function presentationKind(kind: PlcObjectDescriptor['kind']): PLCBlock['kind'] {
  if (kind === 'function_block') return 'functionBlock';
  if (kind === 'organization_block') return 'organizationBlock';
  if (kind === 'data_block') return 'dataBlock';
  if (kind === 'udt' || kind === 'enum' || kind === 'nvt') return 'type';
  return kind === 'global' ? 'global' : kind;
}

/** Split source into importable PLC objects using the same capability rules as delivery and repair. */
export function extractPLCBlocks(code: string, target: PlcTargetProfile = DEFAULT_TARGET): PLCBlock[] {
  if (!code?.trim()) return [];
  return extractPlcObjectDescriptors(code, target).map(descriptor => ({
    name: descriptor.declaredName,
    code: descriptor.source,
    kind: presentationKind(descriptor.kind),
    fileName: descriptor.relativePath,
    descriptor,
  }));
}

/**
 * 谁是这份代码的主块。
 *
 * PROGRAM / OB 是主块的正解。没有时退到可执行块——功能块和函数——而不是直接取第一个：
 * 顶层块顺序是 DUT 在前，于是一份只有 `FB_DualPumpControl` 的程序把枚举
 * `E_PumpState` 当成了主块，界面上标着「主程序 · E_PumpState」。类型定义描述数据，
 * 从不是任何东西的主块。
 */
export function getMainBlockName(blocks: PLCBlock[]): string {
  const main = blocks.find(block => block.kind === 'program' || block.kind === 'organizationBlock')
    || blocks.find(block => block.kind === 'functionBlock' || block.kind === 'function')
    || blocks[0];
  return main?.name || 'plc_program';
}
